import { lazy, FC, Suspense } from "react";
import { Route, Routes, Navigate, useLocation } from "react-router-dom";
import { MasterLayout } from "../../_metronic/layout/MasterLayout";
import TopBarProgress from "react-topbar-progress-indicator";
import { DashboardWrapper } from "../pages/dashboard/DashboardWrapper";
import { MenuTestPage } from "../pages/MenuTestPage";
import { getCSSVariableValue } from "../../_metronic/assets/ts/_utils";
import { WithChildren } from "../../_metronic/helpers";
import BuilderPageWrapper from "../pages/layout-builder/BuilderPageWrapper";

const PrivateRoutes = () => {
  const location = useLocation();
  const ProfilePage = lazy(() => import("../modules/profile/ProfilePage"));
  const WizardsPage = lazy(() => import("../modules/wizards/WizardsPage"));
  const AccountPage = lazy(() => import("../modules/accounts/AccountPage"));
  const WidgetsPage = lazy(() => import("../modules/widgets/WidgetsPage"));
  const ChatPage = lazy(() => import("../modules/apps/chat/ChatPage"));
  const UsersPage = lazy(
    () => import("../modules/apps/user-management/UsersPage"),
  );
  const ListDevices = lazy(() => import("../modules/devices/DevicePage"));
  const PaymentPage = lazy(() => import("../modules/payment/PaymentPage"));
  const TicketPage = lazy(
    () => import("../modules/tickets/components/TicketPage"),
  );
  const UserPages = lazy(() => import("../modules/users/components/UserPage"));
  const CenterDeviceMessagePage = lazy(
    () =>
      import(
        "../modules/centerDeviceMessage/components/CenterDeviceMessagePage"
      ),
  );
  const DashboardPage = lazy(
    () => import("../modules/dashboard/dashboardPage"),
  );
  const ProducerPages = lazy(
    () => import("../modules/producerCenter/components/ProducerCenterPage"),
  );
  const AnalyzeLaboratoryPage = lazy(
    () => import("../modules/laboratory/LaboratoryPage"),
  );
  const DeviceRequestSMSPage = lazy(
    () => import("../modules/deviceRequestSMS/DeviceRequestSMSPage"),
  );
  const LocationPage = lazy(() => import("../modules/location/LocationPage"));
  const FileManagerPage = lazy(
    () => import("../modules/fileManager/FileManagerPage"),
  );
  const UserTokenPage = lazy(
    () => import("../modules/userToken/UserTokenPage"),
  );
  const ApiKeyPage = lazy(() => import("../modules/apiKey/ApiKeyPage"));
  const ListServices = lazy(() => import("../modules/service/ServicePage"));
  const DeviceBuilderPage = lazy(
    () => import("../modules/deviceBuilder/components/DeviceBuilderPage"),
  );

  return (
    <Routes>
      <Route element={<MasterLayout />}>
        {/* Redirect to Dashboard after success login/registartion */}
        <Route
          path="auth/*"
          element={
            <Navigate
              to={{ pathname: "/dashboard", search: location.search }}
            />
          }
        />
        {/* Pages */}
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="builder" element={<BuilderPageWrapper />} />
        <Route path="menu-test" element={<MenuTestPage />} />
        <Route
          path="device-builder"
          element={
            <SuspensedView>
              <DeviceBuilderPage />
            </SuspensedView>
          }
        />
        {/* Lazy Modules */}
        <Route
          path="crafted/pages/profile/*"
          element={
            <SuspensedView>
              <ProfilePage />
            </SuspensedView>
          }
        />
        <Route
          path="devices/*"
          element={
            <SuspensedView>
              <ListDevices />
            </SuspensedView>
          }
        />
        <Route
          path="laboratory/*"
          element={
            <SuspensedView>
              <AnalyzeLaboratoryPage />
            </SuspensedView>
          }
        />
        <Route
          path="service/*"
          element={
            <SuspensedView>
              <ListServices />
            </SuspensedView>
          }
        />
        <Route
          path="/location/*"
          element={
            <SuspensedView>
              <LocationPage />
            </SuspensedView>
          }
        />
        <Route
          path="payment/*"
          element={
            <SuspensedView>
              <PaymentPage />
            </SuspensedView>
          }
        />
        <Route
          path="userToken/*"
          element={
            <SuspensedView>
              <UserTokenPage />
            </SuspensedView>
          }
        />
        <Route
          path="apiKey/*"
          element={
            <SuspensedView>
              <ApiKeyPage />
            </SuspensedView>
          }
        />
        <Route
          path="ticket/*"
          element={
            <SuspensedView>
              <TicketPage />
            </SuspensedView>
          }
        />
        <Route
          path="user/*"
          element={
            <SuspensedView>
              <UserPages />
            </SuspensedView>
          }
        />{" "}
        <Route
          path="producer/*"
          element={
            <SuspensedView>
              <ProducerPages />
            </SuspensedView>
          }
        />
        <Route
          path="centerDeviceMessage/*"
          element={
            <SuspensedView>
              <CenterDeviceMessagePage />
            </SuspensedView>
          }
        />
        <Route
          path="dashboard/"
          element={
            <SuspensedView>
              <DashboardPage />
            </SuspensedView>
          }
        />
        <Route
          path="/deviceRequests/*"
          element={
            <SuspensedView>
              <DeviceRequestSMSPage />
            </SuspensedView>
          }
        />
        <Route
          path="fileManager/*"
          element={
            <SuspensedView>
              <FileManagerPage />
            </SuspensedView>
          }
        />
        <Route
          path="crafted/pages/wizards/*"
          element={
            <SuspensedView>
              <WizardsPage />
            </SuspensedView>
          }
        />
        <Route
          path="crafted/widgets/*"
          element={
            <SuspensedView>
              <WidgetsPage />
            </SuspensedView>
          }
        />
        <Route
          path="crafted/account/*"
          element={
            <SuspensedView>
              <AccountPage />
            </SuspensedView>
          }
        />
        <Route
          path="apps/chat/*"
          element={
            <SuspensedView>
              <ChatPage />
            </SuspensedView>
          }
        />
        {/* <Route
          path="apps/user-management/*"
          element={
            <SuspensedView>
              <UsersPage />
            </SuspensedView>
          }
        /> */}
        {/* Page Not Found */}
        <Route path="*" element={<Navigate to="/error/404" />} />
      </Route>
    </Routes>
  );
};

const SuspensedView: FC<WithChildren> = ({ children }) => {
  const baseColor = getCSSVariableValue("--bs-primary");
  TopBarProgress.config({
    barColors: {
      "0": baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  });
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>;
};

export { PrivateRoutes };
